import { useEffect, useRef } from "react";

import {
  RENDERED_POPOVERS,
  removePopoverData,
  shouldClosePopover,
} from "metabase/common/hooks/use-sequenced-content-close-handler";
import { isElement } from "metabase-types/guards";

export const useNestedModalEscape = ({
  opened,
  onClose,
}: {
  opened: boolean;
  onClose: () => void;
}) => {
  const modalContentRef = useRef<HTMLDivElement | null>(null);
  const popoverDataRef = useRef<{
    contentEl: Element;
    close: (e: KeyboardEvent) => void;
  } | null>(null);

  useEffect(() => {
    if (!opened) {
      if (popoverDataRef.current) {
        removePopoverData(popoverDataRef.current);
        popoverDataRef.current = null;
      }
      return;
    }

    const handleEscape = (event: KeyboardEvent) => {
      if (
        popoverDataRef.current &&
        shouldClosePopover(event, popoverDataRef.current)
      ) {
        popoverDataRef.current.close(event);
      }
    };
    const timeoutId = window.setTimeout(() => {
      const contentEl = modalContentRef.current;
      if (!isElement(contentEl)) {
        return;
      }

      const popoverData = {
        contentEl,
        close: () => onClose(),
      };
      popoverDataRef.current = popoverData;
      RENDERED_POPOVERS.push(popoverData);
      document.addEventListener("keydown", handleEscape);
    }, 0);

    return () => {
      window.clearTimeout(timeoutId);
      document.removeEventListener("keydown", handleEscape);
      if (popoverDataRef.current) {
        removePopoverData(popoverDataRef.current);
        popoverDataRef.current = null;
      }
    };
  }, [onClose, opened]);

  return modalContentRef;
};
